import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, RotateCcw, Check } from "lucide-react";
import { SITE_CONTENT_DEFAULTS, invalidateSiteContent } from "@/hooks/useSiteContent";
import { MaintenancePanel } from "@/components/MaintenancePanel";

const API = process.env.REACT_APP_BACKEND_URL;

// Field render config — keeps this page a simple, declarative form.
const FIELDS = [
  { section: "Hero (Homepage Top)", items: [
    { key: "hero_eyebrow", label: "Eyebrow", type: "input", max: 80 },
    { key: "hero_line1", label: "Headline Line 1", type: "input", max: 40 },
    { key: "hero_line2", label: "Headline Line 2 (green accent)", type: "input", max: 40 },
    { key: "hero_line3", label: "Headline Line 3", type: "input", max: 40 },
    { key: "hero_description", label: "Description paragraph", type: "textarea", rows: 6, max: 2000 },
  ]},
  { section: "Bottom CTA (Homepage Bottom)", items: [
    { key: "cta_eyebrow", label: "Eyebrow", type: "input", max: 80 },
    { key: "cta_heading", label: "Heading", type: "input", max: 120 },
    { key: "cta_description", label: "Description", type: "textarea", rows: 4, max: 1000 },
    { key: "cta_button_text", label: "Button Text", type: "input", max: 40 },
  ]},
  { section: "Footer (Every Page)", items: [
    { key: "footer_tagline", label: "Tagline under logo", type: "textarea", rows: 2, max: 300 },
    { key: "footer_trademark", label: "Trademark notice", type: "textarea", rows: 2, max: 200 },
  ]},
];

export default function AdminSiteContentPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState({ ...SITE_CONTENT_DEFAULTS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [confirmingReset, setConfirmingReset] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    axios.get(`${API}/api/site-content`)
      .then(({ data }) => setValues({ ...SITE_CONTENT_DEFAULTS, ...data }))
      .catch(() => setError("Failed to load current content."))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const setField = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const save = async () => {
    setSaving(true);
    setError("");
    setFeedback("");
    try {
      const { data } = await axios.put(`${API}/api/admin/site-content`, { values });
      setValues({ ...SITE_CONTENT_DEFAULTS, ...data });
      invalidateSiteContent(data);
      setFeedback("Saved. Changes are live for all visitors.");
      setTimeout(() => setFeedback(""), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/admin/site-content/reset`);
      setValues({ ...SITE_CONTENT_DEFAULTS, ...data });
      invalidateSiteContent(data);
      setFeedback("Reset to defaults. Changes are live.");
      setConfirmingReset(false);
      setTimeout(() => setFeedback(""), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
      <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate("/admin")} className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-3" data-testid="back-to-admin">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to Admin
        </button>
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-[#39FF14]" strokeWidth={1.5} />
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14]">Admin</p>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2] mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          Site Content
        </h1>
        <p className="text-[#A1A1A1] text-sm mb-8">
          Edit the hero, bottom CTA, and footer copy. Changes are live for every visitor as soon as you save.
        </p>

        {feedback && (
          <div className="mb-4 p-3 border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-sm font-mono flex items-center gap-2" data-testid="content-feedback">
            <Check className="h-4 w-4" /> {feedback}
          </div>
        )}
        {error && <div className="mb-4 p-3 border border-red-500/30 bg-red-500/10 text-red-400 text-sm font-mono" data-testid="content-error">{error}</div>}

        <MaintenancePanel />

        {FIELDS.map((sec) => (
          <div key={sec.section} className="mb-10">
            <h2 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-4 border-b border-[#1A1A1A] pb-2">
              {sec.section}
            </h2>
            <div className="space-y-4">
              {sec.items.map((f) => (
                <div key={f.key} data-testid={`field-${f.key}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <label className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">{f.label}</label>
                    <span className="font-mono text-[10px] text-[#A1A1A1]/60">{(values[f.key] || "").length}/{f.max}</span>
                  </div>
                  {f.type === "textarea" ? (
                    <textarea
                      value={values[f.key] ?? ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      rows={f.rows || 4}
                      maxLength={f.max}
                      className="w-full bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] px-3 py-2 text-sm font-mono resize-none"
                      data-testid={`input-${f.key}`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={values[f.key] ?? ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      maxLength={f.max}
                      className="w-full bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] px-3 py-2 text-sm font-mono"
                      data-testid={`input-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="sticky bottom-0 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-[#050505] border-t border-[#1A1A1A] flex flex-wrap items-center gap-3">
          <Button onClick={save} disabled={saving} data-testid="content-save" className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-4 disabled:opacity-50">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
          {confirmingReset ? (
            <div className="flex items-center gap-2 border border-red-500/40 bg-red-500/5 px-3 py-2" data-testid="reset-confirm">
              <span className="font-mono text-[10px] uppercase tracking-wider text-red-400">Reset every field to default?</span>
              <Button onClick={reset} disabled={saving} className="bg-red-500 text-white hover:bg-red-600 rounded-none font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 h-auto" data-testid="reset-yes">
                Yes, reset
              </Button>
              <Button onClick={() => setConfirmingReset(false)} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] rounded-none font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 h-auto">
                Cancel
              </Button>
            </div>
          ) : (
            <Button onClick={() => setConfirmingReset(true)} variant="outline" className="bg-transparent border-[#1A1A1A] hover:border-red-500/60 text-[#A1A1A1] hover:text-red-400 rounded-none font-mono text-xs uppercase tracking-wider" data-testid="content-reset">
              <RotateCcw className="h-3 w-3 mr-2" /> Reset to Defaults
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
